import { useEffect, useMemo, useRef, useState } from "react";

import { Card, Stat, TopList, fmtBytes } from "../../components/admin/AdminUI";
import {
  api,
  type AdminBigQueryAllowlistEntry,
  type AdminBigQueryLogEntry,
  type AdminBigQueryMetrics,
  type AdminBigQuerySettings,
} from "../../lib/api";

type TabId = "settings" | "allowlist" | "metrics" | "queries";

const TABS: Array<{ id: TabId; label: string }> = [
  { id: "settings", label: "Settings" },
  { id: "allowlist", label: "Allowlist" },
  { id: "metrics", label: "Metrics" },
  { id: "queries", label: "Recent queries" },
];

type LearningModePresetId = "1d" | "1week" | "1month";

const LEARNING_MODE_PRESETS: Array<{ id: LearningModePresetId; label: string }> = [
  { id: "1d", label: "1d" },
  { id: "1week", label: "1week" },
  { id: "1month", label: "1month" },
];

/** Format an ISO timestamp for compact admin display. */
function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

/** Build the learning-mode expiry timestamp for a preset duration. */
function buildLearningModeExpiry(preset: LearningModePresetId): string {
  const next = new Date();
  if (preset === "1d") {
    next.setDate(next.getDate() + 1);
  } else if (preset === "1week") {
    next.setDate(next.getDate() + 7);
  } else {
    next.setMonth(next.getMonth() + 1);
  }
  return next.toISOString();
}

/** Truncate a SQL snippet without losing its leading context. */
function truncateSql(sql: string, maxLength = 220): string {
  if (sql.length <= maxLength) return sql;
  return `${sql.slice(0, maxLength - 1)}…`;
}

/** Render concise badges for allowlist-entry provenance and review state. */
function AllowlistStatusBadges({ entry }: { entry: AdminBigQueryAllowlistEntry }) {
  return (
    <div className="flex flex-wrap gap-1">
      {entry.auto_added && (
        <span className="rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[11px] text-sky-700">
          Auto-added
        </span>
      )}
      {!entry.reviewed && (
        <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-700">
          Pending review
        </span>
      )}
      {entry.reviewed && entry.auto_added && (
        <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-700">
          Reviewed
        </span>
      )}
      {entry.source && (
        <span className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-600">
          {entry.source}
        </span>
      )}
    </div>
  );
}

/** Admin → BigQuery sub-page. Four tabs: Settings, Allowlist, Metrics, Recent queries. */
export function AdminBigQueryPage() {
  const [tab, setTab] = useState<TabId>("settings");
  return (
    <div>
      <h1 className="mb-2 text-2xl font-semibold">BigQuery</h1>
      <p className="mb-4 text-sm text-slate-500">
        Configure the BigQuery proxy used by artifacts. The allowlist controls
        which datasets artifacts can query.
      </p>
      <nav className="mb-4 flex gap-2 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={
              "px-3 py-1.5 text-sm " +
              (tab === t.id
                ? "border-b-2 border-slate-900 font-semibold text-slate-900"
                : "text-slate-500 hover:text-slate-700")
            }
          >
            {t.label}
          </button>
        ))}
      </nav>
      {tab === "settings" && <SettingsTab />}
      {tab === "allowlist" && <AllowlistTab />}
      {tab === "metrics" && <MetricsTab />}
      {tab === "queries" && <QueriesTab />}
    </div>
  );
}

// ----- Settings tab -------------------------------------------------------

function SettingsTab() {
  const [view, setView] = useState<AdminBigQuerySettings | null>(null);
  const [draft, setDraft] = useState<Partial<AdminBigQuerySettings>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.admin.bigquery.settings
      .get()
      .then((s) => setView(s))
      .catch((e: Error) => setError(e.message));
  }, []);

  async function save() {
    if (!view) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.admin.bigquery.settings.put(draft);
      setView(updated);
      setDraft({});
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function updateLearningMode(patch: Pick<
    AdminBigQuerySettings,
    "allowlist_learning_mode_enabled" | "allowlist_learning_mode_expires_at"
  >) {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.admin.bigquery.settings.put(patch);
      setView(updated);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (view === null) {
    if (error)
      return (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Failed to load settings: {error}
          <button
            type="button"
            className="ml-3 underline"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      );
    return <p className="text-slate-500">Loading…</p>;
  }

  const fields: Array<{
    key: keyof AdminBigQuerySettings;
    label: string;
    type: "number" | "text" | "bool";
    hint?: string;
  }> = [
    { key: "enabled", label: "BigQuery enabled", type: "bool" },
    { key: "max_rows_per_query", label: "Max rows per query", type: "number" },
    {
      key: "max_bytes_per_query",
      label: "Max bytes scanned per query",
      type: "number",
      hint: "Default 100 MB = 104857600 bytes.",
    },
    {
      key: "per_user_queries_per_hour",
      label: "Queries per user / hour",
      type: "number",
    },
    {
      key: "per_user_bytes_per_hour",
      label: "Bytes scanned per user / hour",
      type: "number",
    },
    {
      key: "default_cache_ttl_seconds_cap",
      label: "Max cache TTL artifacts may request (s)",
      type: "number",
    },
    {
      key: "default_project",
      label: "Default project (when omitted by artifact)",
      type: "text",
    },
    { key: "default_location", label: "Default location", type: "text" },
  ];

  return (
    <div className="space-y-4">
      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}
      <section className="border border-slate-200 bg-white p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-sm font-semibold">Allowlist learning mode</h2>
            <div className="mt-2 space-y-1 text-sm text-slate-600">
              <p>
                State:{" "}
                <span className="font-medium text-slate-900">
                  {view.allowlist_learning_mode_enabled ? "Enabled" : "Disabled"}
                </span>
              </p>
              <p>
                Expires at:{" "}
                <span className="font-medium text-slate-900">
                  {formatTimestamp(view.allowlist_learning_mode_expires_at)}
                </span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {LEARNING_MODE_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                disabled={busy}
                onClick={() =>
                  void updateLearningMode({
                    allowlist_learning_mode_enabled: true,
                    allowlist_learning_mode_expires_at: buildLearningModeExpiry(preset.id),
                  })
                }
                className="rounded border border-slate-200 px-3 py-1 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {preset.label}
              </button>
            ))}
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void updateLearningMode({
                  allowlist_learning_mode_enabled: false,
                  allowlist_learning_mode_expires_at: null,
                })
              }
              className="rounded border border-slate-200 px-3 py-1 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Disable
            </button>
          </div>
        </div>
      </section>
      {fields.map((f) => {
        const current = view[f.key];
        const draftVal = (draft as Record<string, unknown>)[f.key as string];
        return (
          <div key={f.key} className="border border-slate-200 bg-white p-4">
            <label className="block text-sm font-semibold">{f.label}</label>
            {f.hint && <p className="mt-1 text-xs text-slate-500">{f.hint}</p>}
            {f.type === "bool" ? (
              <input
                type="checkbox"
                className="mt-2"
                checked={(draftVal ?? current) as boolean}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, [f.key]: e.target.checked }))
                }
              />
            ) : (
              <input
                type={f.type === "number" ? "number" : "text"}
                className="mt-2 w-64 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
                value={String(draftVal ?? current ?? "")}
                onChange={(e) => {
                  const num = Number(e.target.value);
                  const v =
                    f.type === "number"
                      ? e.target.value === "" || !Number.isFinite(num)
                        ? undefined
                        : num
                      : e.target.value === ""
                        ? null
                        : e.target.value;
                  setDraft((d) => {
                    if (v === undefined) {
                      // Numeric field cleared — remove from draft so the Save
                      // button stays disabled and JSON.stringify can't drop it silently.
                      const next = { ...d };
                      delete next[f.key];
                      return next;
                    }
                    return { ...d, [f.key]: v as AdminBigQuerySettings[typeof f.key] };
                  });
                }}
              />
            )}
            <p className="mt-1 text-xs text-slate-500">
              Current: <code>{String(current)}</code>
            </p>
          </div>
        );
      })}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy || Object.keys(draft).length === 0}
          className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

// ----- Allowlist tab ------------------------------------------------------

const PAGE_SIZE = 50;

function AllowlistTab() {
  return <AllowlistInner />;
}

type SortKey = "project" | "dataset" | "table_pattern" | "note" | "added_by" | "added_at";
type EntryView = "all" | "learning" | "pending" | "reviewed_auto_added";

const ENTRY_VIEWS: Array<{ id: EntryView; label: string }> = [
  { id: "all", label: "All" },
  { id: "learning", label: "Learning mode" },
  { id: "pending", label: "Pending review" },
  { id: "reviewed_auto_added", label: "Reviewed auto-added" },
];

/** Return whether an allowlist row belongs in the selected review/provenance view. */
export function allowlistEntryMatchesView(
  entry: AdminBigQueryAllowlistEntry,
  view: EntryView,
): boolean {
  if (view === "learning") return entry.auto_added;
  if (view === "pending") return entry.auto_added && !entry.reviewed;
  if (view === "reviewed_auto_added") return entry.auto_added && entry.reviewed;
  return true;
}

/** Count allowlist rows for each review/provenance view. */
export function allowlistEntryViewCounts(
  entries: AdminBigQueryAllowlistEntry[],
): Record<EntryView, number> {
  return {
    all: entries.length,
    learning: entries.filter((entry) => allowlistEntryMatchesView(entry, "learning")).length,
    pending: entries.filter((entry) => allowlistEntryMatchesView(entry, "pending")).length,
    reviewed_auto_added: entries.filter((entry) =>
      allowlistEntryMatchesView(entry, "reviewed_auto_added"),
    ).length,
  };
}

function AllowlistInner() {
  const [allItems, setAllItems] = useState<AdminBigQueryAllowlistEntry[] | null>(null);
  const [pendingItems, setPendingItems] = useState<AdminBigQueryAllowlistEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [form, setForm] = useState({
    project: "",
    dataset: "",
    table_pattern: "*",
    note: "",
  });
  const [addBusy, setAddBusy] = useState(false);
  const [rowBusyId, setRowBusyId] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [entryView, setEntryView] = useState<EntryView>("all");
  const [sortKey, setSortKey] = useState<SortKey>("added_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "added_at" ? "desc" : "asc");
    }
  }

  // Reset to page 0 when the filter changes.
  useEffect(() => { setCurrentPage(0); }, [filter, entryView]);

  const { displayItems, totalPages, viewCounts } = useMemo(() => {
    if (!allItems) return { displayItems: null, totalPages: 0, viewCounts: null };
    const viewCounts = allowlistEntryViewCounts(allItems);
    const lower = filter.toLowerCase();
    const viewFiltered = allItems.filter((entry) => allowlistEntryMatchesView(entry, entryView));
    const filtered = filter
      ? viewFiltered.filter((e) =>
          [
            e.project,
            e.dataset,
            e.table_pattern,
            e.note ?? "",
            e.added_by,
            e.source ?? "",
            e.triggering_sql ?? "",
            e.triggering_sql_hash ?? "",
          ].some((f) =>
            f.toLowerCase().includes(lower),
          ),
        )
      : viewFiltered;
    const sorted = [...filtered].sort((a, b) => {
      const av =
        sortKey === "added_at"
          ? new Date(a[sortKey]).getTime()
          : (a[sortKey] ?? "").toLowerCase();
      const bv =
        sortKey === "added_at"
          ? new Date(b[sortKey]).getTime()
          : (b[sortKey] ?? "").toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
    const displayItems = sorted.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
    return { displayItems, totalPages, viewCounts };
  }, [allItems, filter, entryView, sortKey, sortDir, currentPage]);

  // Clamp currentPage when totalPages shrinks (e.g. after a delete).
  useEffect(() => {
    if (totalPages > 0 && currentPage >= totalPages) {
      setCurrentPage(totalPages - 1);
    }
  }, [totalPages, currentPage]);

  async function reload() {
    setError(null);
    try {
      const [allResponse, pendingResponse] = await Promise.all([
        api.admin.bigquery.allowlist.listAll(),
        api.admin.bigquery.allowlist.reviewPending(),
      ]);
      setAllItems(allResponse.items);
      setPendingItems(pendingResponse.items);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  useEffect(() => { void reload(); }, []);

  async function add() {
    setAddBusy(true);
    setError(null);
    try {
      await api.admin.bigquery.allowlist.add({
        project: form.project.trim(),
        dataset: form.dataset.trim(),
        table_pattern: form.table_pattern.trim() || "*",
        note: form.note.trim() || undefined,
      });
      setForm({ project: "", dataset: "", table_pattern: "*", note: "" });
      await reload();
      setCurrentPage(0);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAddBusy(false);
    }
  }

  async function remove(id: string) {
    setRowBusyId(id);
    setError(null);
    try {
      await api.admin.bigquery.allowlist.remove(id);
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRowBusyId(null);
    }
  }

  async function review(id: string) {
    setRowBusyId(id);
    setError(null);
    try {
      await api.admin.bigquery.allowlist.review(id);
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRowBusyId(null);
    }
  }

  const SortTh = ({
    col,
    children,
  }: {
    col: SortKey;
    children: React.ReactNode;
  }) => (
    <th className="py-2 pr-2">
      <button
        type="button"
        onClick={() => toggleSort(col)}
        className="flex items-center gap-1 text-xs uppercase text-slate-500 hover:text-slate-800"
      >
        {children}
        <span className="w-3 text-center">
          {sortKey === col ? (sortDir === "asc" ? "↑" : "↓") : ""}
        </span>
      </button>
    </th>
  );

  return (
    <div className="space-y-4">
      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}
      <section className="border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold">Add allowlist entry</h2>
        <div className="mt-2 grid grid-cols-4 gap-2">
          <input
            placeholder="project"
            value={form.project}
            onChange={(e) => setForm({ ...form, project: e.target.value })}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm"
          />
          <input
            placeholder="dataset"
            value={form.dataset}
            onChange={(e) => setForm({ ...form, dataset: e.target.value })}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm"
          />
          <input
            placeholder="table_pattern (default *)"
            value={form.table_pattern}
            onChange={(e) => setForm({ ...form, table_pattern: e.target.value })}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm"
          />
          <input
            placeholder="note (optional)"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm"
          />
        </div>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            disabled={addBusy || !form.project || !form.dataset}
            onClick={() => void add()}
            className="rounded-md bg-slate-900 px-3 py-1 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {addBusy ? "Adding…" : "Add"}
          </button>
        </div>
      </section>
      {pendingItems !== null && pendingItems.length > 0 && (
        <section className="border border-amber-200 bg-amber-50/40 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">Pending review</h2>
            <span className="text-xs text-slate-500">{pendingItems.length} item(s)</span>
          </div>
          <div className="space-y-3">
            {pendingItems.map((entry) => (
              <div key={entry.id} className="border border-amber-200 bg-white p-3">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 space-y-2">
                    <div>
                      <p className="font-mono text-sm text-slate-900">
                        {entry.project}.{entry.dataset}
                      </p>
                      <p className="text-xs text-slate-500">
                        Added by {entry.added_by} at {formatTimestamp(entry.added_at)}
                      </p>
                    </div>
                    <AllowlistStatusBadges entry={entry} />
                    {entry.triggering_sql && (
                      <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 font-mono text-xs text-slate-700">
                        {truncateSql(entry.triggering_sql)}
                      </div>
                    )}
                    {entry.triggering_sql_hash && (
                      <p className="font-mono text-[11px] text-slate-500">
                        SQL hash: {entry.triggering_sql_hash}
                      </p>
                    )}
                    <p className="text-xs text-slate-500">
                      Review: {entry.reviewed ? "Reviewed" : "Pending"}{" "}
                      {entry.reviewed_by ? `by ${entry.reviewed_by}` : ""}
                      {entry.reviewed_at ? ` at ${formatTimestamp(entry.reviewed_at)}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={rowBusyId === entry.id}
                      onClick={() => void review(entry.id)}
                      className="rounded-md bg-slate-900 px-3 py-1 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      disabled={rowBusyId === entry.id}
                      onClick={() => void remove(entry.id)}
                      className="rounded border border-slate-200 px-3 py-1 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      <section>
        <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-sm font-semibold">Entries</h2>
            {viewCounts && (
              <p className="mt-1 text-xs text-slate-500">
                {viewCounts.learning} learning-mode entry(s), {viewCounts.pending} pending review.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap items-center gap-1">
              {ENTRY_VIEWS.map((view) => (
                <button
                  key={view.id}
                  type="button"
                  onClick={() => setEntryView(view.id)}
                  className={
                    "rounded border px-2 py-1 text-xs " +
                    (entryView === view.id
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 text-slate-600 hover:bg-slate-50")
                  }
                >
                  {view.label}
                  {viewCounts && (
                    <span className="ml-1 opacity-75">{viewCounts[view.id]}</span>
                  )}
                </button>
              ))}
            </div>
            <input
              type="search"
              placeholder="Filter by project, dataset, note, SQL…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-72 rounded-md border border-slate-300 px-2 py-1 text-sm"
            />
          </div>
        </div>
        {displayItems === null ? (
          <p className="text-slate-500">Loading…</p>
        ) : allItems?.length === 0 ? (
          <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            No datasets allowlisted yet — every query will be denied until you add one.
          </p>
        ) : displayItems.length === 0 ? (
          <p className="text-sm text-slate-500">No entries match your filter.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500">
              <tr>
                <SortTh col="project">Project</SortTh>
                <SortTh col="dataset">Dataset</SortTh>
                <SortTh col="table_pattern">Pattern</SortTh>
                <th className="py-2 pr-2 text-xs uppercase text-slate-500">Status</th>
                <SortTh col="note">Note</SortTh>
                <SortTh col="added_by">Added by</SortTh>
                <SortTh col="added_at">Added at</SortTh>
                <th />
              </tr>
            </thead>
            <tbody>
              {displayItems.map((e) => (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="py-2 pr-2 font-mono">{e.project}</td>
                  <td className="py-2 pr-2 font-mono">{e.dataset}</td>
                  <td className="py-2 pr-2 font-mono">{e.table_pattern}</td>
                  <td className="py-2 pr-2">
                    <AllowlistStatusBadges entry={e} />
                    {e.auto_added && (
                      <div className="mt-1 space-y-1">
                        {e.triggering_sql && (
                          <p className="max-w-sm break-words font-mono text-[11px] text-slate-500">
                            {truncateSql(e.triggering_sql, 140)}
                          </p>
                        )}
                        {e.triggering_sql_hash && (
                          <p className="font-mono text-[10px] text-slate-400">
                            {e.triggering_sql_hash}
                          </p>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-2 text-slate-600">{e.note ?? "—"}</td>
                  <td className="py-2 pr-2">{e.added_by}</td>
                  <td className="py-2 pr-2 text-slate-500">
                    {formatTimestamp(e.added_at)}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      disabled={rowBusyId === e.id}
                      onClick={() => void remove(e.id)}
                      className="rounded border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {totalPages > 1 && (
          <div className="mt-3 flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => p - 1)}
              disabled={currentPage === 0}
              className="rounded border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-40"
            >
              ← Previous
            </button>
            <span className="text-slate-500">
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={currentPage >= totalPages - 1}
              className="rounded border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

// ----- Metrics tab --------------------------------------------------------

function MetricsTab() {
  return <MetricsInner />;
}

function MetricsInner() {
  const [range, setRange] = useState<"24h" | "7d" | "30d">("24h");
  const [data, setData] = useState<AdminBigQueryMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    api.admin.bigquery
      .metrics(range)
      .then((m) => setData(m))
      .catch((e: Error) => setError(e.message));
  }, [range]);

  return (
    <div className="space-y-4">
      <section className="flex items-center gap-2">
        <label className="text-sm font-semibold text-slate-700">Range</label>
        {(["24h", "7d", "30d"] as const).map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r)}
            className={
              "rounded-md border px-3 py-1 text-sm " +
              (range === r
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 text-slate-600 hover:bg-slate-50")
            }
          >
            {r}
          </button>
        ))}
      </section>
      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}
      {data === null ? (
        <p className="text-slate-500">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-2">
            <Stat label="Queries" value={data.queries.toLocaleString()} />
            <Stat label="Bytes scanned" value={fmtBytes(data.bytes_scanned)} />
            <Stat label="Est. cost" value={`$${data.est_cost_usd.toFixed(2)}`} />
            <Stat label="Denied" value={data.denied_count.toLocaleString()} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Card title="Top actors">
              <TopList
                rows={data.top_actors.map((a) => ({
                  key: a.email,
                  value: a.count.toLocaleString(),
                }))}
              />
            </Card>
            <Card title="Top datasets">
              <TopList
                rows={data.top_datasets.map((d) => ({
                  key: d.name,
                  value: d.count.toLocaleString(),
                }))}
              />
            </Card>
          </div>
          <p className="text-xs text-slate-500">
            Cache hit rate: {(data.cache_hit_rate * 100).toFixed(1)}%
          </p>
        </>
      )}
    </div>
  );
}

// ----- Recent queries tab -------------------------------------------------

function QueriesTab() {
  return <QueriesInner />;
}

function QueriesInner() {
  const [items, setItems] = useState<AdminBigQueryLogEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [drawer, setDrawer] = useState<AdminBigQueryLogEntry | null>(null);

  // Filter state
  const [outcome, setOutcome] = useState("");
  const [actorInput, setActorInput] = useState("");
  const [slugInput, setSlugInput] = useState("");
  const [timeRange, setTimeRange] = useState<"any" | "24h" | "7d" | "30d">("any");

  // Debounced values — sent to the API after 400 ms of inactivity
  const [actorDebounced, setActorDebounced] = useState("");
  const [slugDebounced, setSlugDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setActorDebounced(actorInput), 400);
    return () => clearTimeout(t);
  }, [actorInput]);

  useEffect(() => {
    const t = setTimeout(() => setSlugDebounced(slugInput), 400);
    return () => clearTimeout(t);
  }, [slugInput]);

  // Reset to page 1 whenever any active filter changes
  useEffect(() => {
    setOffset(0);
  }, [outcome, actorDebounced, slugDebounced, timeRange]);

  // Generation counter — discard responses from superseded requests
  const fetchGenRef = useRef(0);

  // Fetch whenever offset or any filter changes
  useEffect(() => {
    const gen = ++fetchGenRef.current;
    setItems(null);
    setError(null);
    api.admin.bigquery
      .queries(limit, offset, {
        outcome: outcome || undefined,
        actor: actorDebounced || undefined,
        slug: slugDebounced || undefined,
        range: timeRange === "any" ? undefined : timeRange,
      })
      .then((r) => { if (fetchGenRef.current === gen) setItems(r.items); })
      .catch((e: Error) => { if (fetchGenRef.current === gen) setError(e.message); });
  }, [offset, outcome, actorDebounced, slugDebounced, timeRange]);

  const hasFilters =
    Boolean(outcome) || Boolean(actorInput) || Boolean(slugInput) || timeRange !== "any";

  function clearFilters() {
    setOutcome("");
    setActorInput("");
    setActorDebounced("");
    setSlugInput("");
    setSlugDebounced("");
    setTimeRange("any");
    setOffset(0);
  }

  const OUTCOMES = [
    "success",
    "disabled",
    "rate_limited",
    "write_attempted",
    "allowlist_denied",
    "cap_exceeded",
    "upstream_error",
  ] as const;

  return (
    <div>
      {/* Filter bar */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        >
          <option value="">All outcomes</option>
          {OUTCOMES.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Actor email…"
          value={actorInput}
          onChange={(e) => setActorInput(e.target.value)}
          className="w-48 rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        />
        <input
          type="text"
          placeholder="Slug…"
          value={slugInput}
          onChange={(e) => setSlugInput(e.target.value)}
          className="w-28 rounded-md border border-slate-300 px-2 py-1.5 font-mono text-sm"
        />
        <div className="flex gap-1">
          {(["any", "24h", "7d", "30d"] as const).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setTimeRange(r)}
              className={
                "rounded-md border px-2.5 py-1.5 text-sm " +
                (timeRange === r
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 text-slate-600 hover:bg-slate-50")
              }
            >
              {r === "any" ? "Any time" : r}
            </button>
          ))}
        </div>
        {hasFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-sm text-slate-500 underline hover:text-slate-800"
          >
            Clear filters
          </button>
        )}
      </div>

      {error && (
        <p className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}
      {items === null ? (
        <p className="text-slate-500">Loading…</p>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="py-2 pr-2">When</th>
                <th className="py-2 pr-2">Actor</th>
                <th className="py-2 pr-2">Slug</th>
                <th className="py-2 pr-2">Datasets</th>
                <th className="py-2 pr-2">Bytes</th>
                <th className="py-2 pr-2">Rows</th>
                <th className="py-2 pr-2">ms</th>
                <th className="py-2 pr-2">Outcome</th>
                <th className="py-2 pr-2">Cached</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="py-2 pr-2 text-slate-500">
                    {new Date(e.created_at).toLocaleString()}
                  </td>
                  <td className="py-2 pr-2">{e.actor_email}</td>
                  <td className="py-2 pr-2 font-mono">
                    {e.slug ? (
                      <a
                        href={`/a/${e.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {e.slug}
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="py-2 pr-2 font-mono">
                    {(e.referenced_tables ?? []).join(", ") || "—"}
                  </td>
                  <td className="py-2 pr-2 text-right">
                    {e.bytes_scanned.toLocaleString()}
                  </td>
                  <td className="py-2 pr-2 text-right">
                    {e.rows_returned.toLocaleString()}
                  </td>
                  <td className="py-2 pr-2 text-right">{e.duration_ms}</td>
                  <td className="py-2 pr-2">
                    <span
                      className={
                        e.outcome === "success"
                          ? "text-emerald-700"
                          : "text-rose-700"
                      }
                    >
                      {e.outcome}
                    </span>
                  </td>
                  <td className="py-2 pr-2">{e.cached ? "yes" : "no"}</td>
                  <td className="py-2 pr-2 text-right">
                    <button
                      type="button"
                      onClick={() => setDrawer(e)}
                      className="rounded border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50"
                    >
                      SQL
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
            <span>
              Page {Math.floor(offset / limit) + 1} · {items.length} rows
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}
                className="rounded border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={items.length < limit}
                onClick={() => setOffset(offset + limit)}
                className="rounded border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
      {drawer && (
        <div
          onClick={() => setDrawer(null)}
          className="fixed inset-0 z-40 flex items-end justify-end bg-slate-900/40"
        >
          <aside
            onClick={(e) => e.stopPropagation()}
            className="z-50 h-full w-[640px] overflow-y-auto bg-white p-6 shadow-xl"
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold">Query detail</h3>
              <button
                type="button"
                onClick={() => setDrawer(null)}
                className="text-sm text-slate-500 hover:text-slate-900"
              >
                Close
              </button>
            </div>
            <dl className="space-y-1 text-sm">
              <Pair k="Outcome" v={drawer.outcome} />
              <Pair k="Actor" v={drawer.actor_email} />
              <div className="flex gap-2">
                <dt className="w-28 shrink-0 text-slate-500">Slug:</dt>
                <dd className="font-mono">
                  {drawer.slug ? (
                    <a
                      href={`/a/${drawer.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {drawer.slug}
                    </a>
                  ) : (
                    "—"
                  )}
                </dd>
              </div>
              <Pair k="SQL hash" v={drawer.sql_hash} />
              <Pair k="Bytes scanned" v={drawer.bytes_scanned.toLocaleString()} />
              <Pair k="Rows returned" v={drawer.rows_returned.toLocaleString()} />
              <Pair k="Duration (ms)" v={String(drawer.duration_ms)} />
              <Pair k="Cached" v={drawer.cached ? "yes" : "no"} />
              {drawer.error_detail && <Pair k="Error" v={drawer.error_detail} />}
            </dl>
            <h4 className="mt-4 text-sm font-semibold">SQL (first 1024 chars)</h4>
            <pre className="mt-1 max-h-[60vh] overflow-auto rounded bg-slate-50 p-3 text-xs">
              {drawer.sql_truncated}
            </pre>
          </aside>
        </div>
      )}
    </div>
  );
}

function Pair({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-28 shrink-0 text-slate-500">{k}:</dt>
      <dd className="font-mono">{v}</dd>
    </div>
  );
}
